import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';
import { getSupabaseClient } from './supabase';

type ClientOption = { supabase?: SupabaseClient };

export type OrderStageGroup = {
  group_key: string;
  label: string;
  sort_order: number;
  statuses: string[];
};

export type OrderStageGroupsResult = {
  groups: OrderStageGroup[];
  /** Statuses not in any group (standalone in filter UI) */
  standaloneStatuses: string[];
  /** Map group_key -> status[] for resolving filter */
  groupKeyToStatuses: Record<string, string[]>;
};

type GroupRow = { group_key: string; label: string; sort_order: number };
type MemberRow = { group_key: string; status: string; sort_order: number };

async function fetchOrderStageGroupsFromTables(
  supabase: SupabaseClient,
  tables: { groups: string; members: string; reference: string },
): Promise<{ data: OrderStageGroupsResult; error: PostgrestError | null }> {
  const [groupsRes, membersRes] = await Promise.all([
    supabase.from(tables.groups).select('group_key,label,sort_order').order('sort_order', { ascending: true }),
    supabase.from(tables.members).select('group_key,status,sort_order').order('sort_order', { ascending: true }),
  ]);
  if (groupsRes.error) return { data: { groups: [], standaloneStatuses: [], groupKeyToStatuses: {} }, error: groupsRes.error };
  if (membersRes.error) return { data: { groups: [], standaloneStatuses: [], groupKeyToStatuses: {} }, error: membersRes.error };

  const groupRows = (groupsRes.data ?? []) as GroupRow[];
  const memberRows = (membersRes.data ?? []) as MemberRow[];
  const statusesInGroup = new Set(memberRows.map((m) => m.status));
  const groupKeyToStatuses: Record<string, string[]> = {};
  const groups: OrderStageGroup[] = groupRows.map((g) => {
    const statuses = memberRows
      .filter((m) => m.group_key === g.group_key)
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((m) => m.status);
    groupKeyToStatuses[g.group_key] = statuses;
    return { group_key: g.group_key, label: g.label, sort_order: g.sort_order, statuses };
  });

  const allStatusesFromRef = await supabase
    .from(tables.reference)
    .select('status')
    .order('stage_index', { ascending: true });
  if (allStatusesFromRef.error) return { data: { groups: [], standaloneStatuses: [], groupKeyToStatuses: {} }, error: allStatusesFromRef.error };
  const allStatuses: string[] = ((allStatusesFromRef.data ?? []) as { status: string }[]).map((r) => r.status);
  const standaloneStatuses = allStatuses.filter((s) => !statusesInGroup.has(s));

  return { data: { groups, standaloneStatuses, groupKeyToStatuses }, error: null };
}

export async function fetchOrderStageGroups(
  options?: ClientOption,
): Promise<{ data: OrderStageGroupsResult; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const eventTables = { groups: 'event_stage_groups', members: 'event_stage_group_members', reference: 'event_status_reference' };
  return fetchOrderStageGroupsFromTables(supabase, eventTables);
}

export type OrderStatusReferenceRow = {
  status: string;
  stage_index: number;
  label: string;
};

export async function fetchOrderStatusReference(
  options?: ClientOption,
): Promise<{ data: OrderStatusReferenceRow[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('event_status_reference')
    .select('status,stage_index,label')
    .order('stage_index', { ascending: true });
  if (error) return { data: [], error };
  return { data: (data ?? []) as OrderStatusReferenceRow[], error: null };
}

/**
 * Moves a stage into one stage group, or out of every group when `group_key` is null. The database does it
 * in one transaction and refuses anyone but a Settings editor with Event Stages edit (42501).
 */
export async function setStageGroup(params: {
  status: string;
  group_key: string | null;
  supabase?: SupabaseClient;
}): Promise<PostgrestError | null> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { error } = await supabase.rpc('event_stage_set_group', { p_status: params.status, p_group_key: params.group_key });
  return error ?? null;
}
